/**
 * ProCalculatorConfig Component
 *
 * Configuration panel for year selection, handling fee type, import data, and G14 toggle
 * Extracted from ProCalculator.tsx (lines 1169-1490)
 */

import { Paper, Title, SimpleGrid, Select, Group, Text, Badge, Alert, Button, Tooltip, Stack, Switch, SegmentedControl, NumberInput, ThemeIcon } from '@mantine/core';
import { IconCalendar, IconCoin, IconInfoCircle, IconChartDots, IconAlertCircle, IconHome, IconBuilding, IconBolt } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import type { ProCalculatorFormValues, HandlingFeeType, TariffCategoryType, BusinessPowerGroupType } from '../../../types/proCalculator';
import { HANDLING_FEE_METADATA } from '../../../types/proCalculator';
import { generateMockBasicValues } from '../../../utils/proCalculator';
import {
  getAvailableYears,
  getYearStatusLabel,
  getYearStatusColor,
  type YearDataStatus,
} from '../../../shared/hooks';
import { YEAR_INFO, YEAR_CHANGE_MESSAGES } from '../constants/proCalculator.constants';

interface ProCalculatorConfigProps {
  formValues: ProCalculatorFormValues;
  yearStatuses: Map<number, YearDataStatus>;
  suggestedYear: number | null;
  suggestedYearReason: string | null;
  yearDataLoading: boolean;
  hasG14PriceData: boolean;
  includeG14: boolean;
  onFormValuesChange: (values: ProCalculatorFormValues) => void;
  onIncludeG14Change: (include: boolean) => void;
  onResultsClear: () => void;
}

export function ProCalculatorConfig({
  formValues,
  yearStatuses,
  suggestedYear,
  suggestedYearReason,
  yearDataLoading,
  hasG14PriceData,
  includeG14,
  onFormValuesChange,
  onIncludeG14Change,
  onResultsClear,
}: ProCalculatorConfigProps) {
  /**
   * Handle tariff category change (HOUSEHOLD/BUSINESS)
   */
  const handleTariffCategoryChange = (value: string) => {
    const newCategory = value as TariffCategoryType;
    const isBusinessNow = newCategory === 'BUSINESS';

    // Determine power group based on existing contracted power
    let newPowerGroup: BusinessPowerGroupType | undefined;
    if (isBusinessNow) {
      const currentPower = formValues.contractedPower || 20; // Default 20 kW
      newPowerGroup = currentPower <= 40 ? 'C1' : 'C2';
    }

    onFormValuesChange({
      ...formValues,
      tariffCategory: newCategory,
      contractedPower: isBusinessNow ? (formValues.contractedPower || 20) : undefined,
      businessPowerGroup: newPowerGroup,
    });

    // Clear results when category changes
    onResultsClear();

    notifications.show({
      title: newCategory === 'HOUSEHOLD' ? 'Taryfy gospodarstw domowych (G)' : 'Taryfy firmowe (C)',
      message: newCategory === 'HOUSEHOLD'
        ? 'Wybrano taryfy dla gospodarstw domowych (G11-G14).'
        : 'Wybrano taryfy firmowe (C11-C22). Podaj moc umowną.',
      color: newCategory === 'HOUSEHOLD' ? 'blue' : 'teal',
      autoClose: 4000,
    });
  };

  /**
   * Handle contracted power change (for business tariffs)
   */
  const handleContractedPowerChange = (value: number | string) => {
    const powerKw = typeof value === 'string' ? parseFloat(value) || 0 : value;
    const newPowerGroup: BusinessPowerGroupType = powerKw <= 40 ? 'C1' : 'C2';

    onFormValuesChange({
      ...formValues,
      contractedPower: powerKw,
      businessPowerGroup: newPowerGroup,
    });

    // Clear results when power changes
    onResultsClear();
  };

  /**
   * Handle year change
   */
  const handleYearChange = (value: string | null) => {
    if (!value) return;

    const newYear = parseInt(value, 10);
    onFormValuesChange({
      ...formValues,
      calculationYear: newYear,
    });

    // Clear results when year changes
    onResultsClear();

    // Get data status for notification
    const yearStatus = yearStatuses.get(newYear);
    const statusInfo = yearStatus?.hasHourlyData
      ? ` Dane godzinowe dostępne (${yearStatus.hourlyDataMonths}/12 miesięcy).`
      : yearStatus?.hasSavedCalculation
        ? ' Dostępna zapisana kalkulacja podstawowa.'
        : '';

    const baseMessage = YEAR_CHANGE_MESSAGES[newYear] || `Rok ${newYear} - historyczne dane.`;

    notifications.show({
      title: `Zmieniono rok na ${newYear}`,
      message: baseMessage + statusInfo,
      color: 'blue',
      autoClose: 5000,
    });
  };

  /**
   * Handle handling fee type change
   */
  const handleHandlingFeeTypeChange = (value: string | null) => {
    if (!value) return;

    const newType = value as HandlingFeeType;

    // Update handling fee type and regenerate parameters
    const updatedMonths = formValues.months.map((month) => ({
      ...month,
      parameters: generateMockBasicValues(month.tariff, month.consumption, newType),
    }));

    onFormValuesChange({
      ...formValues,
      handlingFeeType: newType,
      months: updatedMonths as typeof formValues.months,
    });

    // Clear results when handling fee changes
    onResultsClear();

    const savings = (HANDLING_FEE_METADATA.COMMERCIAL.priceGross - HANDLING_FEE_METADATA.REGULATED.priceGross) * 12;
    notifications.show({
      title: newType === 'REGULATED' ? 'Taryfa sprzedawcy z urzędu' : 'Taryfa komercyjna',
      message: newType === 'REGULATED'
        ? `Oszczędzasz około ${savings.toFixed(0)} zł rocznie - brak opłaty handlowej!`
        : 'Taryfa komercyjna jest droższa - rozważ zmianę na taryfę sprzedawcy z urzędu.',
      color: newType === 'REGULATED' ? 'green' : 'orange',
      autoClose: 5000,
    });
  };

  /**
   * Handle suggested year button click
   */
  const handleUseSuggestedYear = () => {
    if (!suggestedYear) return;

    onFormValuesChange({
      ...formValues,
      calculationYear: suggestedYear,
    });
    onResultsClear();

    notifications.show({
      title: `Zmieniono rok na ${suggestedYear}`,
      message: suggestedYearReason || '',
      color: 'green',
      autoClose: 3000,
    });
  };

  return (
    <>
      {/* Tariff Category Selection (Household vs Business) */}
      <Paper p="xl" shadow="sm" withBorder>
        <Title order={2} mb="lg">
          Rodzaj odbiorcy
        </Title>
        <Stack gap="md">
          <SegmentedControl
            value={formValues.tariffCategory || 'HOUSEHOLD'}
            onChange={handleTariffCategoryChange}
            fullWidth
            size="md"
            data={[
              {
                value: 'HOUSEHOLD',
                label: (
                  <Group gap="xs" justify="center">
                    <IconHome size={18} />
                    <Text size="sm" fw={500}>Gospodarstwo domowe (G)</Text>
                  </Group>
                ),
              },
              {
                value: 'BUSINESS',
                label: (
                  <Group gap="xs" justify="center">
                    <IconBuilding size={18} />
                    <Text size="sm" fw={500}>Firma (C)</Text>
                  </Group>
                ),
              },
            ]}
          />

          {/* Business tariff info and contracted power input */}
          {formValues.tariffCategory === 'BUSINESS' && (
            <Paper withBorder p="md" bg="teal.0">
              <Stack gap="md">
                <Group gap="md" align="flex-start">
                  <ThemeIcon color="teal" size="lg" radius="md">
                    <IconBolt size={20} />
                  </ThemeIcon>
                  <div style={{ flex: 1 }}>
                    <Text size="sm" fw={600} mb={4}>
                      Moc umowna (kW)
                    </Text>
                    <Text size="xs" c="dimmed" mb="sm">
                      Moc przyłączeniowa z umowy. Określa grupę taryfową: C1 (≤40 kW) lub C2 (&gt;40 kW).
                    </Text>
                    <NumberInput
                      value={formValues.contractedPower || 20}
                      onChange={handleContractedPowerChange}
                      min={1}
                      max={10000}
                      step={1}
                      suffix=" kW"
                      allowDecimal
                      decimalScale={1}
                      styles={{ input: { fontWeight: 600 } }}
                    />
                  </div>
                </Group>

                {/* Power group badge */}
                <Group gap="xs">
                  <Badge
                    color={formValues.businessPowerGroup === 'C1' ? 'teal' : 'indigo'}
                    size="lg"
                    variant="light"
                  >
                    Grupa {formValues.businessPowerGroup || 'C1'}: {(formValues.businessPowerGroup || 'C1') === 'C1' ? '≤40 kW' : '>40 kW'}
                  </Badge>
                  <Text size="xs" c="dimmed">
                    {(formValues.businessPowerGroup || 'C1') === 'C1'
                      ? 'Małe firmy, sklepy, biura - taryfy C11, C12a, C12b'
                      : 'Średnie/duże firmy, zakłady - taryfy C21, C22a, C22b'}
                  </Text>
                </Group>

                {/* Business tariff info alert */}
                <Alert variant="light" color="teal" icon={<IconInfoCircle size={16} />}>
                  <Text size="xs">
                    <strong>Taryfy firmowe (C-series):</strong> Oprócz opłat za energię, naliczana jest opłata mocowa
                    za kW mocy umownej. Sprawdź umowę lub fakturę, aby poznać swoją moc umowną.
                  </Text>
                </Alert>
              </Stack>
            </Paper>
          )}

          {/* Household tariff info */}
          {formValues.tariffCategory !== 'BUSINESS' && (
            <Alert variant="light" color="blue" icon={<IconHome size={16} />}>
              <Text size="xs">
                <strong>Taryfy gospodarstw domowych (G-series):</strong> G11 (jednolita), G12 (dzień/noc),
                G12w (weekendowa), G12as (antysmogowa), G13 (3-strefowa), G14 (dynamiczna RCE).
              </Text>
            </Alert>
          )}
        </Stack>
      </Paper>

      {/* Configuration Panel - Year and Handling Fee Selection */}
      <Paper p="xl" shadow="sm" withBorder>
        <Title order={2} mb="lg">
          Ustawienia kalkulacji
        </Title>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl">
          {/* Year Selection */}
          <div>
            <Select
              label={
                <Group gap="xs">
                  <IconCalendar size={16} />
                  <Text size="sm" fw={500}>Rok kalkulacji</Text>
                </Group>
              }
              description="Wybierz rok, dla którego mają być wykonane obliczenia. Opłaty i taryfy różnią się między latami."
              placeholder="Wybierz rok"
              data={getAvailableYears().map((year) => {
                const status = yearStatuses.get(year);
                const statusLabel = getYearStatusLabel(status);
                const yearInfo = YEAR_INFO[year];

                return {
                  value: year.toString(),
                  label: `${year}${yearInfo ? ` - ${yearInfo}` : ''}`,
                  description: statusLabel || undefined,
                };
              })}
              value={formValues.calculationYear.toString()}
              onChange={handleYearChange}
              allowDeselect={false}
            />

            {/* Data availability badges for selected year */}
            {(() => {
              const status = yearStatuses.get(formValues.calculationYear);
              if (!status || yearDataLoading) return null;
              const statusColor = getYearStatusColor(status);
              const statusLabel = getYearStatusLabel(status);

              return statusLabel ? (
                <Group gap="xs" mt="xs">
                  <Badge
                    size="sm"
                    color={statusColor}
                    variant="light"
                    leftSection={statusColor === 'green' ? <IconChartDots size={12} /> : undefined}
                  >
                    {statusLabel}
                  </Badge>
                </Group>
              ) : null;
            })()}

            {/* Year suggestion Alert */}
            {suggestedYear && suggestedYear !== formValues.calculationYear && suggestedYearReason && (
              <Alert
                variant="light"
                color="blue"
                mt="sm"
                icon={<IconInfoCircle size={16} />}
                title="Sugestia"
              >
                <Text size="xs">
                  {suggestedYearReason}
                </Text>
                <Button
                  size="xs"
                  variant="light"
                  mt="xs"
                  onClick={handleUseSuggestedYear}
                >
                  Użyj roku {suggestedYear}
                </Button>
              </Alert>
            )}

            {formValues.calculationYear === 2026 && (
              <Alert variant="light" color="orange" mt="sm" icon={<IconInfoCircle size={16} />}>
                <Text size="xs">
                  <strong>Zmiany w 2026:</strong> Cena energii -1%, ale opłata mocowa +50%, OZE +100%, dystrybucja +9%.
                  Rachunki wzrosną średnio o 3%.
                </Text>
              </Alert>
            )}
          </div>

          {/* Handling Fee Type Selection */}
          <div>
            <Select
              label={
                <Group gap="xs">
                  <IconCoin size={16} />
                  <Text size="sm" fw={500}>Typ opłaty handlowej</Text>
                </Group>
              }
              description="Opłata handlowa to stała miesięczna opłata. Różnica może wynieść do 200 zł rocznie!"
              placeholder="Wybierz typ"
              data={[
                {
                  value: 'REGULATED',
                  label: 'Taryfa sprzedawcy z urzędu - 0 zł/mies. (brak opłaty handlowej)',
                },
                {
                  value: 'COMMERCIAL',
                  label: `Taryfa komercyjna - ${HANDLING_FEE_METADATA.COMMERCIAL.priceGross.toFixed(2)} zł/mies. brutto`,
                },
              ]}
              value={formValues.handlingFeeType}
              onChange={handleHandlingFeeTypeChange}
              allowDeselect={false}
            />
            <Group gap="xs" mt="sm">
              <Badge color={formValues.handlingFeeType === 'REGULATED' ? 'green' : 'orange'} variant="light">
                {formValues.handlingFeeType === 'REGULATED' ? 'Rekomendowane' : 'Droższa opcja'}
              </Badge>
              <Tooltip label="Taryfa sprzedawcy z urzędu (URE) nie pobiera opłaty handlowej (0 zł). Taryfa komercyjna u sprzedawców to ok. 15-20 zł/mies.">
                <Text size="xs" c="dimmed" style={{ cursor: 'help' }}>
                  Oszczędność: ~{((HANDLING_FEE_METADATA.COMMERCIAL.priceGross - HANDLING_FEE_METADATA.REGULATED.priceGross) * 12).toFixed(0)} zł/rok
                </Text>
              </Tooltip>
            </Group>

            {/* Visual Comparison Bar */}
            <Paper withBorder p="sm" mt="md" bg="gray.0">
              <Text size="xs" fw={500} mb="xs">Porównanie roczne opłaty handlowej:</Text>
              <Stack gap={6}>
                {/* Regulated tariff bar */}
                <Group gap="xs" wrap="nowrap">
                  <Text size="xs" w={100} style={{ flexShrink: 0 }}>Sprzedawca URE:</Text>
                  <div style={{
                    height: 16,
                    width: HANDLING_FEE_METADATA.REGULATED.priceGross === 0 ? 4 : `${(HANDLING_FEE_METADATA.REGULATED.priceGross / HANDLING_FEE_METADATA.COMMERCIAL.priceGross) * 100}%`,
                    minWidth: 4,
                    maxWidth: '100%',
                    backgroundColor: 'var(--mantine-color-green-6)',
                    borderRadius: 4,
                    transition: 'width 0.3s ease',
                  }} />
                  <Text size="xs" fw={500} c="green" style={{ flexShrink: 0 }}>
                    0 zł/rok ✓
                  </Text>
                </Group>
                {/* Commercial tariff bar */}
                <Group gap="xs" wrap="nowrap">
                  <Text size="xs" w={100} style={{ flexShrink: 0 }}>Komercyjna:</Text>
                  <div style={{
                    height: 16,
                    width: '100%',
                    backgroundColor: 'var(--mantine-color-orange-6)',
                    borderRadius: 4,
                  }} />
                  <Text size="xs" fw={500} c="orange" style={{ flexShrink: 0 }}>
                    {(HANDLING_FEE_METADATA.COMMERCIAL.priceGross * 12).toFixed(0)} zł/rok
                  </Text>
                </Group>
              </Stack>
              <Text size="xs" c="dimmed" mt="xs" ta="center">
                💰 Oszczędność z taryfą sprzedawcy z urzędu: <strong>{((HANDLING_FEE_METADATA.COMMERCIAL.priceGross - HANDLING_FEE_METADATA.REGULATED.priceGross) * 12).toFixed(0)} zł rocznie</strong>
              </Text>
            </Paper>
          </div>
        </SimpleGrid>
      </Paper>

      {/* G14 Dynamic Tariff Section */}
      <Paper p="md" shadow="xs" withBorder bg={hasG14PriceData ? 'green.0' : 'yellow.0'}>
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <Stack gap="xs" style={{ flex: 1 }}>
              <Group gap="xs">
                <Text size="sm" fw={600}>
                  G14 - Taryfa dynamiczna z cenami RCE
                </Text>
                <Badge color="teal" variant="light" size="sm">
                  NOWOŚĆ 2025
                </Badge>
              </Group>
              <Text size="xs" c="dimmed">
                {hasG14PriceData
                  ? 'Masz dane cenowe RCE. Taryfa G14 będzie uwzględniona w porównaniu.'
                  : 'Brak danych cenowych RCE. Aby uwzględnić G14 w porównaniu, prześlij dane z PSE.'}
              </Text>
            </Stack>
            <Switch
              checked={includeG14 && hasG14PriceData}
              onChange={(event) => onIncludeG14Change(event.currentTarget.checked)}
              disabled={!hasG14PriceData}
              label="Uwzględnij G14"
              size="md"
            />
          </Group>

          {/* Alert: No G14 data available */}
          {!hasG14PriceData && (
            <Alert variant="light" color="yellow" icon={<IconAlertCircle size={16} />}>
              <Text size="xs" fw={500}>
                Dane cenowe RCE dla taryfy G14 nie są jeszcze dostępne dla wybranego okresu.
                Dane są aktualizowane centralnie na podstawie publikacji PSE.
              </Text>
            </Alert>
          )}

          {/* Info: How G14 works */}
          {hasG14PriceData && (
            <Alert variant="light" color="teal" icon={<IconInfoCircle size={16} />}>
              <Text size="xs">
                <strong>Jak działa G14:</strong> Ceny energii zmieniają się co godzinę na podstawie RCE z PSE.
                Największe oszczędności uzyskasz przesuwając zużycie na godziny z najniższymi cenami (zwykle w nocy i weekendy).
              </Text>
            </Alert>
          )}
        </Stack>
      </Paper>
    </>
  );
}
